import { useReactiveVar } from '@apollo/client'
import { themeVar } from '@graphql/variables/SharedVariable'
import { Progress } from 'antd'
import { useEffect, useState } from 'react'
import { darkTheme, lightTheme, Theme } from 'src/styles/theme'
import styled from 'styled-components'
import { formatToLocaleString, networkNameById } from '../../../services/UtilService'
import { RockpoolItem, RockpoolStatus } from '../../../types/RockpoolTypes'
import CardBase from '../../design-system/CardBase'
import Icon, { CardTypes } from '../Icon'

export interface RockPoolCardProps {
  item: RockpoolItem
  isStatusBar?: boolean
}

export default function RockPoolCard({ item, isStatusBar = true }: RockPoolCardProps) {
  const { buyersCount, target, targetPrice, paymentToken, poolProgress, isErc721Available, reservePrice } = item
  const { chainId } = item.target
  const isLostPool = !item.isErc721Available
  const isSuccessPool = item.status !== RockpoolStatus.CREATED
  const isCompletedPool = item.status === RockpoolStatus.CREATED && Number(item.poolProgress.replace('%', '')) === 100
  const themeStyle = useReactiveVar(themeVar)

  const [activeTheme, setActiveTheme] = useState<Theme>(themeStyle === 'dark' ? darkTheme : lightTheme)
  const [rockpoolUrl, setRockpoolUrl] = useState<string>(`/rockpool/${chainId}/detail/${item.id}`)
  useEffect(() => {
    setActiveTheme(themeStyle === 'dark' ? darkTheme : lightTheme)
  }, [themeStyle])

  useEffect(() => {
    if (!item.listed) {
      setRockpoolUrl(`/rockpool/${chainId}/detail/private/${item.creator}/${item.id}`)
    }
  }, [chainId, item.creator, item.id, item.listed])

  return (
    <CardBase
      totalMembers={buyersCount}
      totalNfts={target.boxItemCount || 1}
      network={chainId}
      animationUrl={target.metadata?.animation_url}
      imageUrl={String(target.metadata?.image)}
      urlRedirect={rockpoolUrl}
    >
      <Footer>
        <Info>
          <h4>{`${target.collection.name} #${target.tokenId}`}</h4>
          <div>
            <h2>{`${isErc721Available ? 'Target Price' : 'Accumulated'}`}</h2>
            <Icon name={networkNameById(chainId)} size={12} />
            <div>
              <span>{formatToLocaleString(isErc721Available ? targetPrice : reservePrice, 2)}</span>
              <small>{paymentToken.symbol}</small>
            </div>
          </div>
        </Info>
        {isStatusBar && !isLostPool && !isSuccessPool && !isCompletedPool && (
          <StatusBar>
            <div>
              <Icon name={CardTypes.poolProgress} size={18} />
              <span>Pool Progress </span>
            </div>
            <div>
              <ProgressBar>
                <Progress showInfo percent={Number(formatToLocaleString(poolProgress.replace('%', ''), 2))} />
              </ProgressBar>
            </div>
          </StatusBar>
        )}
        {isStatusBar && isLostPool && !isSuccessPool && (
          <StatusBar>
            <div>
              <Icon name={CardTypes.poolLost} size={18} />
              <span>Pool Lost</span>
            </div>
            <div>
              <ProgressBar>
                <Progress strokeColor={activeTheme.colors.red.main} percent={Number(poolProgress.replace('%', ''))} />
              </ProgressBar>
            </div>
          </StatusBar>
        )}
        {isStatusBar && isCompletedPool && (
          <StatusBar>
            <div>
              <Icon name={CardTypes.poolSuccessful} size={18} />
              <span>Pool Progress </span>
            </div>
            <div>
              <ProgressBar>
                <Progress percent={100} format={() => `${Number(formatToLocaleString(100, 2))}%`} status='success' />
              </ProgressBar>
            </div>
          </StatusBar>
        )}
        {isStatusBar && isSuccessPool && (
          <StatusBar>
            <div>
              <Icon name={CardTypes.poolNftSold} size={18} />
              <span>Winning Pool</span>
            </div>
          </StatusBar>
        )}
      </Footer>
    </CardBase>
  )
}

const { Footer, Info, StatusBar, ProgressBar } = {
  Footer: styled.div`
    background: ${props => props.theme.colors.gray[1]};
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
  `,
  Info: styled.div`
    padding: 8px 16px;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 28px 24px;

    > h4 {
      font-style: normal;
      font-weight: 500;
      font-size: 16px;
      line-height: normal;
      color: ${props => props.theme.colors.black};
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }
    > div {
      display: flex;
      gap: 8px;
      align-items: center;
      margin-bottom: 8px;
      > h2 {
        font-style: normal;
        font-weight: 400;
        font-size: 12px;
        line-height: 133%;
        color: ${props => props.theme.colors.black};
      }
      > div {
        display: flex;
        gap: 4px;
        > span {
          font-weight: 500;
          font-size: 16px;
          line-height: 100%;
          color: ${props => props.theme.colors.black};
          max-width: 120px;
          width: auto;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
          width: 100%;
          display: block;
        }

        > small {
          font-style: normal;
          font-weight: 400;
          font-size: 12px;
          line-height: 133%;
          color: ${props => props.theme.colors.black};
        }
      }
    }
  `,
  StatusBar: styled.div`
    display: grid;
    grid-template-columns: 1fr 120px;
    gap: 8px;
    align-items: center;
    border-bottom-left-radius: 16px;
    border-bottom-right-radius: 16px;
    padding-left: 16px;
    padding-right: 16px;
    height: 56px;
    background: ${props => props.theme.colors.gray[0]};

    > div:nth-child(1) {
      display: grid;
      align-items: center;
      grid-template-columns: 14px 1fr;
      gap: 8px;
      > span {
        font-size: 14px;
        line-height: 133%;
        font-weight: 400;
        font-style: normal;
        color: ${props => props.theme.colors.black};
      }
    }
  `,
  ProgressBar: styled.div`
    .ant-progress-line {
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      justify-content: flex-end;
    }

    .ant-progress-outer {
      display: grid;
      align-items: center;
      margin-right: 0;
      padding-right: 0;
    }

    .ant-progress.ant-progress-line.ant-progress-status-normal.ant-progress-show-info.ant-progress-default {
      span {
        font-style: normal;
        font-weight: 500;
        font-size: 13px;
        line-height: 33px;
        margin-left: 8px;
        margin-right: 8px;
      }
    }

    .ant-progress-inner {
      background: ${props => props.theme.colors.white};
      border: 1px solid ${props => props.theme.colors.gray[0]};
      margin-top: 2px;
    }

    .ant-progress-text {
      color: ${props => props.theme.colors.black};
      font-size: 16px;
      line-height: 16px;
      font-weight: 500;
      text-align: right;
      margin-left: 8px !important;
      margin-right: 0 !important;
      width: auto;
    }

    .ant-progress-bg {
      height: 8px !important;
    }
  `
}
